import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion } from 'framer-motion';
import { Play, Film } from 'lucide-react';
// Declare YouTube types
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}
export const AboutSection = () => {
  const [activeVideo, setActiveVideo] = useState<number | null>(null);
  const [players, setPlayers] = useState<{
    [key: number]: any;
  }>({});
  const [isAPIReady, setIsAPIReady] = useState(false);
  const playerRefs = useRef<{
    [key: number]: HTMLDivElement | null;
  }>({});
  const trailers = [{
    title: 'Official Experience Trailer',
    description: 'Step into the grandeur of the Sikh Kingdom',
    videoId: '2yMWvJinVgk',
    thumbnail: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png'
  }, {
    title: 'ਅਧਿਕਾਰਤ ਅਨੁਭਵ ਟ੍ਰੇਲਰ',
    description: 'ਸਿੱਖ ਰਾਜ ਦੀ ਸ਼ਾਨ ਵਿੱਚ ਕਦਮ ਰੱਖੋ',
    videoId: 'erCbP8ed8Hs',
    thumbnail: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png'
  }];
  // Load YouTube IFrame API
  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      // If there are existing script tags, insert before the first one
      // Otherwise, append to document head
      if (firstScriptTag && firstScriptTag.parentNode) {
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      } else {
        document.head.appendChild(tag);
      }
      window.onYouTubeIframeAPIReady = () => {
        setIsAPIReady(true);
      };
    } else {
      setIsAPIReady(true);
    }
  }, []);
  // Create player when video is activated
  useEffect(() => {
    if (!isAPIReady || activeVideo === null || players[activeVideo]) return;
    const playerElement = playerRefs.current[activeVideo];
    if (!playerElement) return;
    const player = new window.YT.Player(playerElement, {
      videoId: trailers[activeVideo].videoId,
      width: '100%',
      height: '100%',
      playerVars: {
        autoplay: 1,
        mute: 1,
        rel: 0,
        modestbranding: 1,
        controls: 1
      },
      events: {
        onReady: (event: any) => {
          event.target.playVideo();
        },
        onStateChange: (event: any) => {
          if (event.data === window.YT.PlayerState.PLAYING) {
            event.target.unMute();
          }
        }
      }
    });
    setPlayers(prev => ({
      ...prev,
      [activeVideo]: player
    }));
  }, [isAPIReady, activeVideo, players, trailers]);
  const handleVideoClick = (index: number) => {
    // Clean up previous player if exists
    if (activeVideo !== null && players[activeVideo]) {
      players[activeVideo].destroy();
      setPlayers(prev => {
        const newPlayers = {
          ...prev
        };
        delete newPlayers[activeVideo];
        return newPlayers;
      });
    }
    setActiveVideo(index);
  };
  return <section id="about" className="relative py-20 md:py-28 bg-black" data-id="element-55">
      <div className="container mx-auto px-4" data-id="element-56">
        <div className="max-w-6xl mx-auto" data-id="element-57">
          {/* Header */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="text-center mb-24" data-id="element-58">
            <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-59">
              An Immersive Chronicle
            </p>
            <h2 className="font-serif text-5xl md:text-7xl text-white mb-8" data-id="element-60">
              A KINGDOM
              <br data-id="element-61" />
              <span className="text-amber-400" data-id="element-62">REBORN</span>
            </h2>
            <div className="w-24 h-px bg-amber-400/30 mx-auto" data-id="element-63"></div>
          </motion.div>
          {/* Main content grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-24" data-id="element-64">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-65">
              <p className="text-white/80 text-lg leading-relaxed mb-6 font-light" data-id="element-66">
                Step into the court of Maharaja Ranjit Singh, where history
                unfolds not behind glass, but all around you. Through walls that
                transform, floors that transport, and flavors that resurrect a
                golden age.
              </p>
              <p className="text-white/60 leading-relaxed font-light" data-id="element-67">
                This is not theater. This is not dining. This is a portal to
                1825 Lahore—where every sense connects you to the grandeur of
                the Sikh Empire.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} data-id="element-68">
              <div className="border-l border-white/10 pl-8" data-id="element-69">
                <h3 className="font-serif text-2xl text-amber-400 mb-4" data-id="element-70">
                  The Experience
                </h3>
                <ul className="space-y-4 text-white/70 font-light" data-id="element-71">
                  <li className="flex items-start" data-id="element-72">
                    <span className="text-amber-400 mr-3" data-id="element-73">—</span>
                    <span data-id="element-74">
                      5-7 courses synchronized with immersive projections
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-75">
                    <span className="text-amber-400 mr-3" data-id="element-76">—</span>
                    <span data-id="element-77">
                      360° visual storytelling across walls, floor, and tables
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-78">
                    <span className="text-amber-400 mr-3" data-id="element-79">—</span>
                    <span data-id="element-80">Royal Punjabi cuisine from world-renowned chefs</span>
                  </li>
                  <li className="flex items-start" data-id="element-81">
                    <span className="text-amber-400 mr-3" data-id="element-82">—</span>
                    <span data-id="element-83">90 minutes of complete immersion</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
          {/* ROM Heritage Section */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="mb-24" data-id="element-84">
            <div className="border border-amber-400/30 p-8 md:p-12 bg-black/40" data-id="element-85">
              <div className="text-center mb-8" data-id="element-86">
                <h3 className="font-serif text-3xl text-amber-400 mb-4" data-id="element-87">
                  Building on a Legacy
                </h3>
                <div className="w-16 h-px bg-amber-400/30 mx-auto" data-id="element-88"></div>
              </div>
              <div className="max-w-4xl mx-auto" data-id="element-89">
                <p className="text-white/80 text-lg leading-relaxed mb-6 font-light text-center" data-id="element-90">
                  This immersive experience honors the groundbreaking "Art of
                  the Sikh Kingdoms" exhibition at the Royal Ontario Museum in
                  August 2000—the second most popular exhibition in ROM history.
                </p>
                <p className="text-white/60 leading-relaxed font-light text-center" data-id="element-91">
                  Where that exhibition brought artifacts behind glass, we bring
                  the entire kingdom to life around you—transforming scholarship
                  into sensation, and history into an unforgettable evening.
                </p>
              </div>
            </div>
          </motion.div>
          {/* Trailers Section */}
          <div id="trailers" className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-92">
            {trailers.map((trailer, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: index * 0.2
          }} className="group" data-id="element-93">
                <div className="relative" data-id="element-94">
                  {activeVideo === index ? <div className="aspect-video bg-black rounded-lg overflow-hidden" data-id="element-95">
                      <div ref={el => playerRefs.current[index] = el} className="w-full h-full" data-id="element-96"></div>
                    </div> : <div className="relative aspect-video cursor-pointer overflow-hidden rounded-lg" onClick={() => handleVideoClick(index)} data-id="element-97">
                      <img src={trailer.thumbnail} alt={trailer.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-98" />
                      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-300" data-id="element-99"></div>
                      <div className="absolute inset-0 flex items-center justify-center" data-id="element-100">
                        <motion.div whileHover={{
                    scale: 1.1
                  }} whileTap={{
                    scale: 0.95
                  }} className="w-20 h-20 rounded-full bg-amber-400/90 backdrop-blur-sm flex items-center justify-center border-2 border-white/20 group-hover:bg-amber-400 transition-colors duration-300" data-id="element-101">
                          <Play className="w-10 h-10 text-black ml-1" fill="currentColor" data-id="element-102" />
                        </motion.div>
                      </div>
                      <div className="absolute top-4 left-4" data-id="element-103">
                        <div className="flex items-center gap-2 bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full border border-white/10" data-id="element-104">
                          <Film className="w-4 h-4 text-amber-400" data-id="element-105" />
                          <span className="text-white text-xs tracking-wider" data-id="element-106">
                            TRAILER
                          </span>
                        </div>
                      </div>
                    </div>}
                  <div className="mt-6" data-id="element-107">
                    <h3 className="font-serif text-2xl text-white mb-2" data-id="element-108">
                      {trailer.title}
                    </h3>
                    <p className="text-white/60 font-light" data-id="element-109">
                      {trailer.description}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
};